import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tables } from "@/integrations/supabase/types";

type Agent = Tables<"agents">;
type DbFormField = Tables<"form_fields">;

interface FormField extends DbFormField {
  fieldType?: string;
}

export interface FormFieldValues {
  title: string;
  fieldType: string;
  instructions?: string;
  is_required: boolean;
  options?: string;
  position?: number;
  field_type: string;
}

type FieldSection = "in_conversation" | "post_conversation" | "pre-session";

interface FormFieldFormProps {
  agent: Agent;
  fieldSection: FieldSection;
  editingField?: FormField | null;
  onSubmit: (values: FormFieldValues) => Promise<void>;
  onCancel: () => void;
  renderButtons?: (props: {
    isLoading: boolean;
    isEditing: boolean;
    fieldSection: FieldSection;
  }) => React.ReactNode;
}

const FormFieldForm: React.FC<FormFieldFormProps> = ({
  agent,
  fieldSection,
  editingField,
  onSubmit,
  onCancel,
  renderButtons,
}) => {
  const isEditing = !!editingField;
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<FormFieldValues>({
    resolver: zodResolver(z.object({
      title: z.string().min(1, "Field title is required"),
      fieldType: z.string().min(1, "Field type is required"),
      instructions: z.string().optional(),
      is_required: z.boolean().default(false),
      options: z.string().optional(),
      position: z.number().optional(),
      field_type: z.string().default("in_conversation"),
    })),
    defaultValues: {
      title: "",
      fieldType: "text",
      instructions: "",
      is_required: false,
      options: "",
      position: 0,
      field_type: fieldSection,
    },
  });

  // Set form values when editing a field
  useEffect(() => {
    if (editingField) {
      const optionsValue = Array.isArray(editingField.options) 
        ? editingField.options.join('\n') 
        : editingField.options || '';
        
      form.reset({
        title: editingField.title || "",
        fieldType: editingField.fieldType || editingField.field_type || "text",
        instructions: editingField.instructions || "",
        is_required: editingField.is_required || false,
        options: optionsValue,
        position: editingField.position || 0,
        field_type: fieldSection,
      });
    } else {
      form.reset({
        title: "",
        fieldType: "text",
        instructions: "",
        is_required: false,
        options: "",
        position: 0,
        field_type: fieldSection,
      });
    }
  }, [editingField, fieldSection, form]);

  const handleSubmit = async (data: FormFieldValues) => {
    setIsLoading(true);
    try {
      await onSubmit(data);
    } finally {
      setIsLoading(false);
    }
  };

  const watchFieldType = form.watch("fieldType");

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          <div className="grid gap-5 md:grid-cols-2">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Field Label</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter field label"
                      {...field}
                      className="border-gray-200 focus:shadow-[0_1px_2px_rgba(0,0,0,0.05),0_0_0_2px_rgba(138,163,217,0.5)]"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="fieldType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Answer Type</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger className="border-gray-200 focus:shadow-[0_1px_2px_rgba(0,0,0,0.05),0_0_0_2px_rgba(138,163,217,0.5)]">
                        <SelectValue placeholder="Select answer type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="textarea">Text Area</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="select">Dropdown</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </div>

          {/* Only show AI instructions for in_conversation and post_conversation, NOT for pre-session (splash page) */}
          {(fieldSection === "in_conversation" || fieldSection === "post_conversation") && (
            <FormField
              control={form.control}
              name="instructions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {fieldSection === "post_conversation"
                      ? "Extraction Instructions"
                      : "Instructions for your AI agent"}
                  </FormLabel>
                  {fieldSection === "in_conversation" && (
                    <p className="text-sm text-gray-500 mb-2">
                      Optional instructions to help your AI agent understand when and how to use this field during conversations.
                    </p>
                  )}
                  <FormControl>
                    <Input
                      placeholder={
                        fieldSection === "post_conversation"
                          ? "Instructions for how to extract this field"
                          : "Enter instructions for your AI agent"
                      }
                      {...field}
                      className="border-gray-200 focus:shadow-[0_1px_2px_rgba(0,0,0,0.05),0_0_0_2px_rgba(138,163,217,0.5)]"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          )}

          {watchFieldType === "select" && (
            <FormField
              control={form.control}
              name="options"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Options</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter options, one per line"
                      {...field}
                      className="border-gray-300 focus:shadow-[0_1px_2px_rgba(0,0,0,0.05),0_0_0_2px_rgba(138,163,217,0.5)] resize-none bg-white"
                      style={{
                        minHeight: 60,
                        maxHeight: 140,
                        height: "auto",
                        overflowY: "auto",
                        padding: 10,
                      }}
                      rows={1}
                      onInput={(e) => {
                        const t = e.target as HTMLTextAreaElement;
                        t.style.height = "auto";
                        t.style.height = `${t.scrollHeight}px`;
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="is_required"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 bg-gray-50">
                <FormControl>
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-[#8aa3d9] focus:ring-2 focus:ring-[rgba(138,163,217,0.5)]"
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-gray-700">
                    Required field
                  </FormLabel>
                  <p className="text-sm text-gray-500">
                    {fieldSection === "post_conversation"
                      ? "This field should always be provided by the agent"
                      : "Users must answer this field before the conversation can be completed"}
                  </p>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="field_type"
            render={({ field }) => (
              <FormItem className="hidden">
                <FormControl>
                  <input type="hidden" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Render buttons if renderButtons prop is provided */}
          {renderButtons &&
            renderButtons({
              isLoading,
              isEditing,
              fieldSection,
            })}
        </form>
      </Form>
    );
};

export default FormFieldForm; 